import { OrderItem, OrderUpdatePayload } from 'types/api/orders.type';

/**
 * Required header fields for sending an order, in the order they are presented
 * to the user. Each tuple is [payloadKey, i18nLabelKey].
 *
 * The labels are i18n keys; callers may pass them through `t(...)`.
 */
export const SEND_ORDER_REQUIRED_HEADER_FIELDS: ReadonlyArray<
  readonly [keyof OrderUpdatePayload, string]
> = [
  ['season', 'catalogue.season'],
  ['zone', 'zone.label'],
  ['customerCode', 'customer.label'],
  ['shippingAddressCode', 'orderHeader.shippingAddress'],
  ['goodsDestinationCode', 'orderHeader.goodsDestination'],
  ['orderTakenOn', 'orderHeader.orderTakenOn'],
  ['deliveryCode', 'orderHeader.deliveryCode'],
  ['collectionCode', 'customerForm.collection'],
];

/** Red/blue (off-season / in-season) reorders ship from stock and send no delivery period. */
const REORDER_WAREHOUSE_CODES = ['2', '3'];

const isBlank = (val: unknown): boolean =>
  val === undefined || val === null || (typeof val === 'string' && val.trim() === '');

export type SendValidationResult = {
  isValid: boolean;
  /** i18n keys describing each missing field (header fields + items message). */
  missingFields: string[];
};

/**
 * Validates that an order is ready to be sent to the backend.
 *
 * Returns the list of missing field labels (i18n keys) so the caller can
 * render them and toast a summary. An empty list means the order is valid.
 *
 * Required:
 *  - Header: season, zone, customerCode, shippingAddressCode,
 *    goodsDestinationCode, orderTakenOn, deliveryCode, collectionCode.
 *    `deliveryCode` is not required for a red/blue reorder (warehouse 2 or 3): the backend sends no
 *    delivery period for it.
 *  - Items:  at least one item with totalQuantity > 0
 */
export const validateOrderForSend = (
  payload: Partial<OrderUpdatePayload>,
  items: ReadonlyArray<Pick<OrderItem, 'totalQuantity'>> = [],
  warehouseInternalCode?: string | null
): SendValidationResult => {
  const missingFields: string[] = [];
  const isReorder = REORDER_WAREHOUSE_CODES.includes((warehouseInternalCode ?? '').trim());

  SEND_ORDER_REQUIRED_HEADER_FIELDS.forEach(([key, label]) => {
    if (key === 'deliveryCode' && isReorder) return;
    if (isBlank(payload[key])) missingFields.push(label);
  });

  const hasItemWithQty = items.some((it) => (it.totalQuantity ?? 0) > 0);
  if (!hasItemWithQty) {
    // Single combined message — items may exist but be empty or every item zero-qty.
    missingFields.push('order.atLeastOneProduct');
  }

  return { isValid: missingFields.length === 0, missingFields };
};
